import navConfig from '../../../constants/navigationLinks';
import NavigationLink from '../../../interfaces/NavigationLink';
import { generalSettings, moduleSettings } from '../../Settings/services';

export interface NavigablePage {
  label: string;
  path: string;
}

const toAbsolute = (path: string): string =>
  path.startsWith('/') ? path : `/${path}`;

const isInternal = (path: string): boolean =>
  !!path && !path.startsWith('http');

const flattenNav = (
  list: NavigationLink[],
  parentLabel = ''
): NavigablePage[] => {
  const pages: NavigablePage[] = [];
  for (const item of list) {
    if (item.locked || item.comingSoon) continue;
    const label = parentLabel ? `${parentLabel} › ${item.label}` : item.label;
    if (typeof item.content === 'string') {
      if (!item.externalLink && isInternal(item.content)) {
        pages.push({ label, path: toAbsolute(item.content) });
      }
    } else if (Array.isArray(item.content)) {
      pages.push(...flattenNav(item.content, label));
    }
  }
  return pages;
};

/**
 * Every internal page a shortcut can point to: flattened navigation plus the
 * settings tiles. De-duplicated by path and sorted by label.
 *
 * Pass the permission-filtered `navigationLinks` from `useNavigationLink` so
 * the picker only offers pages the current user can actually reach.
 */
export const getNavigablePages = (
  filteredNav: NavigationLink[] = navConfig
): NavigablePage[] => {
  const navPages = flattenNav(filteredNav);
  const settingsPages = [...generalSettings, ...moduleSettings]
    .filter((setting) => !setting.external && isInternal(setting.path))
    .map((setting) => ({
      label: `Settings › ${setting.label}`,
      path: toAbsolute(setting.path),
    }));

  const byPath = new Map<string, NavigablePage>();
  [...navPages, ...settingsPages].forEach((page) => byPath.set(page.path, page));
  return Array.from(byPath.values()).sort((a, b) =>
    a.label.localeCompare(b.label)
  );
};
